import React, { useCallback, useRef } from 'react';
import {
	Button,
	Checkbox,
	FormControlLabel,
	List,
	Popover,
	Stack,
	Typography,
} from '@mui/material';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import {
	GRID_CHECKBOX_SELECTION_FIELD,
	gridColumnDefinitionsSelector,
	useGridApiContext,
	useGridSelector,
} from '@mui/x-data-grid';
import { applyColumnOrder, isColumnReorderLocked } from '@/components/table/columnOrder';
import { useColumnControl } from '@/context/columnControlContext';
import ColumnReorderListItem from './ColumnReorderListItem';

// Delay between each step of an arrow-button move; roughly the dnd library's own move animation
const ARROW_MOVE_STEP_MS = 250;

const ColumnReorderMenu = ({ open, anchorEl, onClose }) => {
	const {
		hiddenColumns,
		toggleColumn,
		showAllColumns,
		hideColumns,
		columnOrder,
		replaceColumnOrder,
		resetOrder,
	} = useColumnControl();
	const apiRef = useGridApiContext();
	const gridColumns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
	const sensorApiRef = useRef(null);

	// Custom dnd sensor: captures the sensor API so the arrow buttons can run a
	// keyboard-style drag, which gives them the same animation as dragging
	const arrowSensor = useCallback((api) => {
		sensorApiRef.current = api;
	}, []);

	// Order comes from the context so a drop shows immediately; the grid only picks it up
	// after its own effects run. Its selection column isn't user-managed.
	const columns = applyColumnOrder(
		gridColumns.filter((column) => column.field !== GRID_CHECKBOX_SELECTION_FIELD),
		columnOrder,
	);

	const hideableColumns = columns.filter((column) => column.hideable !== false);

	const hideableVisibleCount = hideableColumns.filter(
		(column) => !hiddenColumns.includes(column.field),
	).length;
	const allHideableVisible =
		hideableColumns.length > 0 && hideableVisibleCount === hideableColumns.length;
	const someHideableVisible = hideableVisibleCount > 0 && !allHideableVisible;

	const handleToggleAllVisibility = (event) => {
		if (event.target.checked) {
			showAllColumns();
			return;
		}
		hideColumns(hideableColumns.map((column) => column.field));
	};

	const handleDragEnd = (result) => {
		const { source, destination } = result;
		if (!destination || source.index === destination.index) return;

		const nextOrder = columns.map((column) => column.field);
		const [removed] = nextOrder.splice(source.index, 1);
		nextOrder.splice(destination.index, 0, removed);
		replaceColumnOrder(columns, nextOrder);
	};

	// Index of the nearest unlocked column in `direction`, or -1 if there isn't one
	const findMoveTarget = (index, direction) => {
		for (let i = index + direction; i >= 0 && i < columns.length; i += direction) {
			if (!isColumnReorderLocked(columns[i])) return i;
		}
		return -1;
	};

	// Locked columns in between are passed over, then pinned back in place on drop
	const handleMoveBy = (field, index, direction) => () => {
		const preDrag = sensorApiRef.current?.tryGetLock(field);
		if (!preDrag) return;

		const actions = preDrag.snapLift();
		const move = direction < 0 ? actions.moveUp : actions.moveDown;
		let remainingSteps = Math.abs(findMoveTarget(index, direction) - index);

		// Pause between the lift, each move and the drop so every step's animation plays out
		const step = () => {
			if (!actions.isActive()) return;
			if (remainingSteps === 0) {
				actions.drop();
				return;
			}
			move();
			remainingSteps -= 1;
			setTimeout(step, ARROW_MOVE_STEP_MS);
		};
		setTimeout(step, ARROW_MOVE_STEP_MS);
	};

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			// The drag preview is portaled outside the popover and keeps focus while dragging
			disableEnforceFocus
			disableScrollLock
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			slotProps={{
				paper: {
					onMouseDown: (event) => event.stopPropagation(),
					// The list is the only scroll container; dnd doesn't support nested ones
					sx: { overflowY: 'hidden' },
				},
			}}
		>
			<Stack sx={{ width: 320, py: 1.5, px: 1.5 }} spacing={1}>
				<Typography variant='subtitle2'>Columns</Typography>
				<Typography variant='caption' color='text.secondary'>
					Drag or use arrows to reorder. Order is saved in this browser.
				</Typography>
				<DragDropContext onDragEnd={handleDragEnd} sensors={[arrowSensor]}>
					<Droppable droppableId='data-grid-column-reorder'>
						{(droppableProvided) => (
							<List
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
								dense
								disablePadding
								sx={{ maxHeight: 360, overflowY: 'auto' }}
							>
								{columns.map((column, index) => {
									const reorderLocked = isColumnReorderLocked(column);

									return (
										<ColumnReorderListItem
											key={column.field}
											column={column}
											index={index}
											visible={
												column.hideable === false ||
												!hiddenColumns.includes(column.field)
											}
											canMoveUp={
												!reorderLocked && findMoveTarget(index, -1) !== -1
											}
											canMoveDown={
												!reorderLocked && findMoveTarget(index, 1) !== -1
											}
											onToggleVisibility={() => toggleColumn(column.field)}
											onMoveUp={handleMoveBy(column.field, index, -1)}
											onMoveDown={handleMoveBy(column.field, index, 1)}
										/>
									);
								})}
								{droppableProvided.placeholder}
							</List>
						)}
					</Droppable>
				</DragDropContext>
				<Stack
					direction='row'
					alignItems='center'
					justifyContent='space-between'
					spacing={1}
				>
					<FormControlLabel
						sx={{ ml: 0.5, mr: 0 }}
						control={
							<Checkbox
								size='small'
								checked={allHideableVisible}
								indeterminate={someHideableVisible}
								disabled={hideableColumns.length === 0}
								onChange={handleToggleAllVisibility}
							/>
						}
						label='Show/Hide All'
					/>
					<Button variant='text' size='small' onClick={resetOrder}>
						Reset order
					</Button>
				</Stack>
			</Stack>
		</Popover>
	);
};

export default ColumnReorderMenu;
